import { useState } from 'react'
import { generateShopSSOToken } from '@/lib/pocketbase'
import Button from '@/components/Button'
import LoadingSpinner from '@/components/LoadingSpinner'

interface ShopPromoModalProps {
  isOpen: boolean
  onClose: () => void
}

const ShopPromoModal = ({ isOpen, onClose }: ShopPromoModalProps) => {
  const [isLoadingSSO, setIsLoadingSSO] = useState(false)

  const handleGoToShop = async () => {
    try {
      setIsLoadingSSO(true)
      const redirectUrl = await generateShopSSOToken()
      window.open(redirectUrl, '_blank')
      onClose()
    } catch (error) {
      console.error('Failed to redirect to shop:', error)
      // Still close the modal, user can try again from navbar
      onClose()
    } finally {
      setIsLoadingSSO(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-md text-center bg-gradient-to-b from-primary/10 to-base-100">
        {/* Close button */}
        <button
          className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
          onClick={onClose}
          disabled={isLoadingSSO}
        >
          ✕
        </button>

        {/* Celebration icon */}
        <div className="text-6xl mb-4">
          🎉
        </div>

        {/* Headline */}
        <h3 className="font-bold text-2xl mb-6 text-primary">
          You've earned points!
        </h3>

        {/* Messages */}
        <div className="space-y-4 mb-8 px-4">
          <p className="text-lg">
            Go spend your points in the store now! Purchased items can be claimed at the <span className="font-semibold">Capstone reception</span>.
          </p>
          <div className="bg-secondary/20 rounded-lg p-4">
            <p className="text-lg font-bold text-secondary">
              1 booq point = 8 oinks in the store
            </p>
            <p className="text-sm mt-1 opacity-80">
              So go spend your points now!
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="modal-action justify-center gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoadingSSO}
          >
            Maybe Later
          </Button>
          <Button
            variant="primary"
            onClick={handleGoToShop}
            disabled={isLoadingSSO}
          >
            {isLoadingSSO ? (
              <>
                <LoadingSpinner className="w-4 h-4 mr-2" />
                Loading...
              </>
            ) : (
              'Go to Shop'
            )}
          </Button>
        </div>
      </div>

      {/* Backdrop */}
      <div
        className="modal-backdrop bg-black/50"
        onClick={onClose}
      />
    </div>
  )
}

export default ShopPromoModal
